import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { WmsLayer } from '@app/models/map/WmsLayer';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import * as L from 'leaflet';
import { catchError, debounceTime, EMPTY, finalize, Subject } from 'rxjs';
import { ModalComponent } from '../info-modal/modal/modal.component';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { LayerSelectorComponent } from './layer-selector/layer-selector.component';

@Component({
	selector: 'app-map',
	standalone: true,
	imports: [LeafletModule, LayerSelectorComponent, ModalComponent, LoadingSpinnerComponent],
	templateUrl: './map.component.html',
	styleUrl: './map.component.css',
})
export class MapComponent {
	@Input() modalId: string = '';
	@Input() customLayers!: WmsLayer[];
	@Input() filters!: { [filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] } };
	@Input() showLayerSelectorModal: boolean = true;

	filtersDebouncer = new Subject<any>();

	@Output() customLayerSelected = new EventEmitter<string>();
	@Output() mapLoaded = new EventEmitter<L.Map>();

	selectedCustomLayer!: WmsLayer;
	initialBaseLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
		maxZoom: 18,
		crossOrigin: 'anonymous',
	});
	showMapLoadingSpinner = false;

	// leaflet properties
	map!: L.Map;
	visibleLayers: L.Layer[] = [];
	options = {
		zoom: 9, // sets default zoom
		zoomControl: true,
		center: L.latLng(44.45, 11.0), // sets default centering point
		attributionControl: false, // removes Leaflet image and logs,
		zoomSnap: 1,
		zoomDelta: 1,
	};

	// "EPSG:3785","+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs"

	layersControl = {
		baseLayers: {
			Nessuno: L.tileLayer(''),
			'Open street map': L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
				maxZoom: 18,
				crossOrigin: 'anonymous',
			}),
			// 'Open street map (bianco e nero)': tileLayer('https://tiles.stadiamaps.com/tiles/stamen_toner/{z}/{x}/{y}.png', { maxZoom: 18 }),
			/* 'Open street map (Humanitarian)': L.tileLayer('https://b.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
				maxZoom: 18,
				attribution: '© OpenStreetMap Contributors. Tiles courtesy of Humanitarian OpenStreetMap Team',
				crossOrigin: 'anonymous',
			}),
			'Humanitarian (transparent)': L.tileLayer('https://b.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
				maxZoom: 18,
				attribution: '© OpenStreetMap Contributors. Tiles courtesy of Humanitarian OpenStreetMap Team',
				crossOrigin: 'anonymous',
				opacity: 0.5,
			}), */
			'Google Maps (Strade)': L.tileLayer('https://{s}.google.com/vt/lyrs=m&hl=it&x={x}&y={y}&z={z}', {
				maxZoom: 18,
				subdomains: ['mt', 'mt0', 'mt1', 'mt2', 'mt3'],
				crossOrigin: 'anonymous',
			}),
			'Google Maps (Ibrida)': L.tileLayer('https://{s}.google.com/vt/lyrs=s,h&hl=it&x={x}&y={y}&z={z}', {
				maxZoom: 18,
				subdomains: ['mt', 'mt0', 'mt1', 'mt2', 'mt3'],
				crossOrigin: 'anonymous',
			}),
			'Google Maps (Satellite)': L.tileLayer('https://{s}.google.com/vt/lyrs=s&hl=it&x={x}&y={y}&z={z}', {
				maxZoom: 18,
				subdomains: ['mt', 'mt0', 'mt1', 'mt2', 'mt3'],
				crossOrigin: 'anonymous',
			}),
			'Google Maps (Terreno)': L.tileLayer('https://{s}.google.com/vt/lyrs=p&hl=it&x={x}&y={y}&z={z}', {
				maxZoom: 18,
				subdomains: ['mt', 'mt0', 'mt1', 'mt2', 'mt3'],
				crossOrigin: 'anonymous',
			}),
		},
		overlays: {
			'Località abitate': L.tileLayer.wms('api/wms', {
				maxZoom: 18,
				attribution: '...',
				format: 'image/png',
				transparent: true,
				layers: 'RERCTA_Viabilita:LOC_ABITATE',
				styles: 'RERCTA_Viabilita:LOC_ABITATE',
			}),
		},
	};

	/**
	 *
	 */
	constructor(private http: HttpClient) {
		this.filtersDebouncer.pipe(debounceTime(800)).subscribe({
			next: (filters: {
				[filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] };
			}) => {
				this.updateLayers(this.map, this.selectedCustomLayer, filters);
			},
		});
	}

	/**
	 * Reacts to changes in the layersInfo and filters Inputs:
	 *  - layersInfo changes when parent updates the list setting the selected one
	 *  - filters changes when some filter is updated in the parent.
	 * In both cases we need to update the layers shown on the map
	 * @param changes
	 */
	ngOnChanges(changes: SimpleChanges) {
		this.setSelectedLayerInfo();

		if (this.map) {
			if (changes['filters']) {
				this.filtersDebouncer.next(changes['filters'].currentValue);
			} else {
				this.updateLayers(this.map, this.selectedCustomLayer, this.filters);
			}
		}
	}

	/**
	 * Adds initial base layer and attaches callback to 'baselayerchange' event
	 * @param map
	 */
	onMapReady(map: L.Map) {
		this.map = map;
		this.map.addLayer(this.initialBaseLayer);
		this.updateLayers(this.map, this.selectedCustomLayer, this.filters);

		this.map.on('baselayerchange', () => {
			this.map.removeLayer(this.initialBaseLayer);
		});

		this.map.on('dragstart', () => {
			this.map.getContainer().classList.add('leaflet-dragging');
		});

		this.map.on('dragend', () => {
			this.map.getContainer().classList.remove('leaflet-dragging');
		});

		this.mapLoaded.emit(this.map);
	}

	/**
	 * Adds a pop up with info on the clicked point
	 * @param event
	 */
	onMapClick = (event: L.LeafletMouseEvent) => {
		if (this.showMapLoadingSpinner) {
			return;
		}

		this.showMapLoadingSpinner = true;

		this.selectedCustomLayer
			.getPopup(event.latlng, this.filters, this.http, this.map.getZoom())
			.pipe(
				catchError((error: any) => {
					this.showMapLoadingSpinner = false;
					// console.error(error);
					return EMPTY;
				}),
				finalize(() => {
					this.showMapLoadingSpinner = false;
				}),
			)
			.subscribe((popup: L.Popup) => {
				this.showMapLoadingSpinner = false;
				this.map.openPopup(popup);
			});
	};

	/**
	 * set selected layer info and propagates name of selected filter to parent
	 * @param name name of the selected layer
	 */
	public onLayerSelection = (name: string) => {
		this.customLayerSelected.emit(name);
	};

	/**
	 * updates the selectedLayerInfo setting it equal to the one with selected = true in the layersInfo array
	 * @param name name of selected layer
	 */
	public setSelectedLayerInfo = () => {
		this.selectedCustomLayer = this.customLayers.find((l) => l.selected) || ({} as WmsLayer);
	};

	/**
	 * Updates leaflet layer array (maps selectedLayer.themes to a tileLayer.wms)
	 */
	private updateLayers = (
		map: L.Map,
		selectedLayerInfo: WmsLayer,
		filters: { [filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] } },
	) => {
		map.closePopup();
		// remove previously added tile layers
		for (const layer of this.visibleLayers) {
			layer.removeEventListener('load');
			map.removeLayer(layer);
		}

		this.visibleLayers = [];

		// Start loading spinner if some layer has to be rendered.
		// Layer 'load' event will remove the spinner
		if (selectedLayerInfo.themes.length > 0) {
			map.dragging.disable();
			map.touchZoom.disable();
			map.doubleClickZoom.disable();
			map.scrollWheelZoom.disable();
			map.boxZoom.disable();
			map.keyboard.disable();
			this.showMapLoadingSpinner = true;
		}

		let wmsLayers = this.selectedCustomLayer.getAsWms(filters);
		wmsLayers.forEach((l) => {
			l.on('load', this.onLayerLoad);
			map.addLayer(l);
			this.visibleLayers.push(l);
		});
	};

	private onLayerLoad = () => {
		this.map.dragging.enable();
		this.map.touchZoom.enable();
		this.map.doubleClickZoom.enable();
		this.map.scrollWheelZoom.enable();
		this.map.boxZoom.enable();
		this.map.keyboard.enable();
		this.showMapLoadingSpinner = false;
	};
}
